// ./src/components/features/StaffPay/staffPayHelpers.ts
//
// Display helpers for STAFF PAY (#A4.7): status label / colour, minutes as "7h 30m", a money formatter bound to the
// event's currency. ⚠ Display only — every amount comes from the API (one implementation of the pay rules); nothing
// here computes pay.

import { STAFF_PAY_STATUS, StaffPayPerson, StaffPayShift, isPayableShift } from "@/utils/api";
import { formatCurrency } from "@/utils";

export interface StaffPayStatusMeta {
  label: string;
  color: string;
  /** What the status means for the money — the tooltip. */
  hint: string;
}

export const STAFF_PAY_STATUS_META: Record<string, StaffPayStatusMeta> = {
  [STAFF_PAY_STATUS.EXPECTED]: {
    label: "Expected",
    color: "gray",
    hint: "Not checked in and out yet — the scheduled shift, less any break.",
  },
  [STAFF_PAY_STATUS.ACTUAL]: {
    label: "Worked",
    color: "teal",
    hint: "Checked in and out — the time actually worked, less any break.",
  },
  [STAFF_PAY_STATUS.NO_SHOW]: {
    label: "No-show",
    color: "red",
    hint: "Marked a no-show — earns nothing, bonus included.",
  },
  [STAFF_PAY_STATUS.UNPAID]: {
    label: "Unpaid",
    color: "gray",
    hint: "No hourly rate on the shift or its role — a volunteer, not paid staff.",
  },
};

export const staffPayStatusMeta = (status?: string | null): StaffPayStatusMeta =>
  (status && STAFF_PAY_STATUS_META[status]) || STAFF_PAY_STATUS_META[STAFF_PAY_STATUS.EXPECTED];

/** 450 → "7h 30m"; 45 → "45m"; null → "—". */
export const formatPayMinutes = (minutes?: number | null): string => {
  if (minutes == null || !Number.isFinite(Number(minutes))) {
    return "—";
  }

  const m = Math.max(0, Math.round(Number(minutes)));
  const h = Math.floor(m / 60);
  const rest = m % 60;

  if (h === 0) {
    return `${rest}m`;
  }

  return rest === 0 ? `${h}h` : `${h}h ${rest}m`;
};

/** A money formatter bound to the event's currency. */
export const moneyFormatter =
  (currencyCode?: string | null) =>
  (amount?: number | null): string =>
    formatCurrency(Number(amount) || 0, currencyCode || undefined);

/** One person's finished, not-yet-paid shifts — what "Mark paid" can take. */
export const payableShifts = (person: StaffPayPerson): StaffPayShift[] => person.shifts.filter(isPayableShift);

/**
 * The payments linked to one person's shifts, each with the shifts it paid (Undo works per payment).
 * ⚠ No amount here: a shift edited after it was paid no longer adds up to its payment — the person's `paid` (from the
 * payments themselves) is the only paid figure to show.
 */
export const paymentsOf = (person: StaffPayPerson): { budgetPaymentId: string; shifts: StaffPayShift[] }[] => {
  const byId = new Map<string, StaffPayShift[]>();

  person.shifts.forEach((shift) => {
    if (shift.budgetPaymentId) {
      byId.set(shift.budgetPaymentId, [...(byId.get(shift.budgetPaymentId) ?? []), shift]);
    }
  });

  return [...byId.entries()].map(([budgetPaymentId, shifts]) => ({ budgetPaymentId, shifts }));
};
